/**
 * PaletteColour string enum values matching the shared KMP PaletteColour enum.
 * These map to the `colour` field (string) stored in the database.
 */
export type PaletteColour =
  | 'RED'
  | 'BLUE'
  | 'INDIGO'
  | 'GREEN'
  | 'LIME'
  | 'PURPLE'
  | 'ORANGE'
  | 'YELLOW'
  | 'PINK'
  | 'TEAL'
  | 'BROWN'
  | 'GREY'

export const COLOUR_MAP: Record<PaletteColour, { name: string; hex: string; lightHex: string; tailwind: string }> = {
  RED: { name: 'Red', hex: '#F44336', lightHex: '#fde8e8', tailwind: 'bg-red-500' },
  BLUE: { name: 'Blue', hex: '#2196F3', lightHex: '#dbeafe', tailwind: 'bg-blue-500' },
  INDIGO: { name: 'Indigo', hex: '#3F51B5', lightHex: '#e0e7ff', tailwind: 'bg-indigo-500' },
  GREEN: { name: 'Green', hex: '#4CAF50', lightHex: '#dcfce7', tailwind: 'bg-green-500' },
  LIME: { name: 'Lime', hex: '#AFB42B', lightHex: '#ecfccb', tailwind: 'bg-lime-600' },
  PURPLE: { name: 'Purple', hex: '#9C27B0', lightHex: '#f3e8ff', tailwind: 'bg-purple-500' },
  ORANGE: { name: 'Orange', hex: '#FF9800', lightHex: '#fff7ed', tailwind: 'bg-orange-500' },
  YELLOW: { name: 'Yellow', hex: '#E0B200', lightHex: '#fef9c3', tailwind: 'bg-yellow-500' },
  PINK: { name: 'Pink', hex: '#C2185B', lightHex: '#fce7f3', tailwind: 'bg-pink-600' },
  TEAL: { name: 'Teal', hex: '#009688', lightHex: '#ccfbf1', tailwind: 'bg-teal-500' },
  BROWN: { name: 'Brown', hex: '#795548', lightHex: '#efebe9', tailwind: 'bg-amber-700' },
  GREY: { name: 'Grey', hex: '#757575', lightHex: '#f3f4f6', tailwind: 'bg-gray-500' },
}

/**
 * Full enum, in KMP order — keep stable: the DB stores these strings and the
 * legacy `colourId` (ACCENT_COLORS) indexes into this order. Do not reorder.
 */
export const ALL_COLOURS: PaletteColour[] = Object.keys(COLOUR_MAP) as PaletteColour[]

/**
 * Colours a user may assign to a class / scheme / category identity.
 *
 * Indigo is "the app" (canvas, rail active, primary) and is retired from the
 * identity rotation, matching mobile. INDIGO stays in the enum/COLOUR_MAP so
 * existing data stored as indigo still resolves; it is simply not offered in
 * pickers. 11 hues remain assignable.
 */
export const ASSIGNABLE_COLOURS: PaletteColour[] = ALL_COLOURS.filter((c) => c !== 'INDIGO')

export const DEFAULT_COLOUR: PaletteColour = 'RED'

export function getColourHex(colour: string | undefined | null): string {
  return COLOUR_MAP[colour as PaletteColour]?.hex ?? COLOUR_MAP.RED.hex
}

export function getColour(colour: string | undefined | null) {
  return COLOUR_MAP[colour as PaletteColour] ?? COLOUR_MAP.RED
}

// Legacy compatibility - will be removed once all consumers are migrated
export const ACCENT_COLORS = ALL_COLOURS.map((key, index) => ({
  id: index,
  name: COLOUR_MAP[key].name,
  hex: COLOUR_MAP[key].hex,
  tailwind: COLOUR_MAP[key].tailwind,
}))

export type AccentColor = (typeof ACCENT_COLORS)[number]

export function getAccentColor(colourId: number | undefined | null): AccentColor {
  return ACCENT_COLORS[colourId ?? 0] ?? ACCENT_COLORS[0]!
}

export function getAccentColorHex(colourId: number | undefined | null): string {
  return getAccentColor(colourId).hex
}
